import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Check, KeyRound, Store, TriangleAlert } from 'lucide-react';

/**
 * Claiming a vendor invite.
 *
 * The same machinery as the practitioner claim page — the account already exists with no
 * password, and this is the moment the maker chooses how she signs in — but the copy is hers:
 * a storefront, not a practice, and no charge for opening one.
 */
const STEPS = [
  'Choose how you sign in',
  'Accept the Terms, Privacy Policy and Community Guidelines',
  'Agree to the Vendor Terms and Marketplace Guidelines',
  'Tell us about your store',
  'We review it, and your storefront opens',
];

export default function VendorInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This link is missing its invitation code.');
      return;
    }
    axios
      .get(`${API}/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'This invitation link is not valid.'));
  }, [token]);

  const land = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    toast.success('Your account is yours — a few things to read and agree to next');
    window.location.href = '/dashboard/vendor-onboarding';
  };

  const claimWithPassword = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Those two passwords don't match");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/invite/${token}/password`, { password });
      land(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not set your password');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#f8f2e4] flex items-center justify-center p-6"
           data-testid="vendor-invite-error">
        <div className="max-w-md w-full bg-white border border-[#ede4ce] rounded-2xl p-8 text-center">
          <TriangleAlert className="w-9 h-9 text-[#c4622d] mx-auto mb-4" />
          <h1 className="text-base md:text-lg font-semibold text-[#4a4035] mb-2">
            This link won&apos;t open
          </h1>
          <p className="text-sm text-[#7a6e62] mb-6">{error}</p>
          <Button onClick={() => navigate('/login')} data-testid="vendor-invite-signin-btn">
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f2e4] flex items-center justify-center p-6"
         data-testid="vendor-invite-page">
      <div className="max-w-lg w-full bg-white border border-[#ede4ce] rounded-2xl overflow-hidden">
        <div className="px-8 pt-8 pb-5 border-b border-[#f0ebe0]">
          <Store className="w-8 h-8 text-[#c4622d] mb-3" />
          <h1 className="text-lg md:text-xl font-semibold text-[#4a4035]">
            {invite ? `Welcome, ${(invite.full_name || '').split(' ')[0]}` : 'Claim your account'}
          </h1>
          <p className="text-sm text-[#7a6e62] mt-1">
            Your storefront account at 11/11 Collective Connection
          </p>
        </div>

        <div className="px-8 py-6">
          <ol className="mb-7 space-y-2">
            {STEPS.map((s, i) => (
              <li key={s} className="flex gap-3 text-sm text-[#7a6e62]"
                  data-testid={`vendor-invite-step-${i}`}>
                <span className="w-5 h-5 rounded-full bg-[#f8f2e4] text-[#c4622d] text-xs
                                 font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i === 0 ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span className={i === 0 ? 'text-[#4a4035] font-medium' : ''}>{s}</span>
              </li>
            ))}
          </ol>

          <form onSubmit={claimWithPassword} className="space-y-4">
            <div>
              <Label htmlFor="vendor-pw" className="text-sm text-[#4a4035]">Set a password</Label>
              <Input
                id="vendor-pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="mt-1"
                data-testid="vendor-invite-password"
              />
            </div>
            <div>
              <Label htmlFor="vendor-pw2" className="text-sm text-[#4a4035]">And again</Label>
              <Input
                id="vendor-pw2"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                className="mt-1"
                data-testid="vendor-invite-password-confirm"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[#c4622d] hover:bg-[#a9511f]"
                    data-testid="vendor-invite-claim-btn">
              <KeyRound className="w-4 h-4 mr-2" />
              {busy ? 'Claiming…' : 'Claim my account'}
            </Button>
          </form>

          <p className="text-xs text-[#b0a899] mt-5">
            There is no charge to open a storefront — the platform takes a percentage of each sale,
            set out in full before you finish.
          </p>
        </div>
      </div>
    </div>
  );
}
